import React, { useEffect, useState } from "react";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import "./AddEditGuestUser.css";
import MainLayout from "components/layout/main-layout/MainLayout";
import { titles } from "constants/titles";
import CustomButton from "components/widgets/custom-button/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, isSessionTimeExpired, isValidPassword } from "utility";
import alerts from "constants/alerts";
import { useHistory, useLocation } from "react-router-dom";
import { addGuestUser, updateGuestUser } from "redux/ManageGuestUser/actions";
import CustomInput from "components/widgets/custom-input/CustomInput";
import SessionExpiredPopup from "components/layout/session-expired-popup/SessionExpiredPopup";

const AddEditGuestUser = () => {
  const location = useLocation();
  const mode = location?.state?.mode || "add"; // "add" or "edit"
  const guestDetail = location?.state?.guestDetail;
  
  const [guestUserState, setGuestUserState] = useState({
    name: "",
    password: "",
    guestUserID: null,
  });
  const [verificationPopUp, setVerificationPopUp] = useState(false);
  
  // Mobile-like validation states
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [errorState, setErrorState] = useState({
    show: false,
    fieldErrors: {
      name: false,
      password: false,
    },
    fieldMessages: {
      name: "",
      password: "",
    },
  });
  
  const accountHolderData = useSelector(
    (store) => store?.userDetail?.accountHolderData
  );
  const loading = useSelector((store) => store?.guestUser?.loading);
  const dispatch = useDispatch();
  const history = useHistory();

  // Initialize validation hook for snackbar
  const { snackbarState, dismissSnackbar, onApiResponse,  handleApiResponse } = useAdvancedValidation();

  useEffect(() => {
    if (mode === "edit" && guestDetail) {
      setGuestUserState({
        guestUserID: guestDetail?.guest_user_id,
        name: guestDetail?.guest_user_name || "",
        password: guestDetail?.password || "",
      });
    } else {
      setGuestUserState({
        guestUserID: null,
        name: "",
        password: "",
      });
    }
  }, [mode, guestDetail]);

  const onClose = () => {
    history.goBack();
  };

  const onSaveChanges = async () => {
    setValidationTriggered(true);
    
    let timeDifference = await isSessionTimeExpired();
    let hasErrors = false;
    let newErrorState = {
      show: true,
      fieldErrors: { name: false, password: false },
      fieldMessages: { name: "", password: "" },
    };

    // Validate name
    if (isEmpty(guestUserState?.name)) {
      hasErrors = true;
      newErrorState.fieldErrors.name = true;
      newErrorState.fieldMessages.name = alerts?.enter_guest_user_name;
    }

    // Validate password
    if (isEmpty(guestUserState?.password)) {
      hasErrors = true;
      newErrorState.fieldErrors.password = true;
      newErrorState.fieldMessages.password = alerts?.enter_guest_user_password;
    } else if (!isValidPassword(guestUserState?.password, 6)) {
      hasErrors = true;
      newErrorState.fieldErrors.password = true;
      newErrorState.fieldMessages.password = alerts?.password_minimum_message;
    }

    setErrorState(newErrorState);

    if (hasErrors) {
      // onApiResponse("Incorrect or Missing Details Below", "error");
      return;
    }

    if (timeDifference) {
      setVerificationPopUp(true);
      return;
    }

    const params = {
      dispatch,
      payload: {
        guest_user_id: mode === "edit" ? guestUserState?.guestUserID : undefined,
        guest_user_name: guestUserState?.name,
        password: guestUserState?.password,
      },
      onClose: () => {
        setTimeout(() => {
          history.goBack();
        }, 500);
      },
      history,
      onApiResponse: handleApiResponse,
    };

    if (mode === "edit") {
      updateGuestUser(params);
    } else {
      addGuestUser(params);
    }
  };

  const onValueChange = (type, e) => {
    let newText = e?.target?.value;
    setGuestUserState((prev) => {
      return { ...prev, [type]: newText };
    });

    // Only validate if validation has been triggered (after submit click)
    if (validationTriggered) {
      // Real-time validation for the current field
      let fieldError = false;
      let fieldMessage = "";

      if (type === "name") {
        if (isEmpty(newText)) {
          fieldError = true;
          fieldMessage = alerts?.enter_guest_user_name;
        }
      } else if (type === "password") {
        if (isEmpty(newText)) {
          fieldError = true;
          fieldMessage = alerts?.enter_guest_user_password;
        } else if (!isValidPassword(newText, 6)) {
          fieldError = true;
          fieldMessage = alerts?.password_minimum_message;
        }
      }

      // Update field error state
      setErrorState((prev) => {
        const newErrorState = {
          ...prev,
          fieldErrors: { ...prev.fieldErrors, [type]: fieldError },
          fieldMessages: { ...prev.fieldMessages, [type]: fieldMessage },
        };

        // Check if there are any other field errors after this update
        const hasOtherErrors = Object.keys(newErrorState.fieldErrors).some(fieldKey =>
          fieldKey !== type && newErrorState.fieldErrors?.[fieldKey]
        );
      if (fieldError && !snackbarState.show) {
        onApiResponse("Incorrect or Missing Details Below", "error");
        } else if (!fieldError && !hasOtherErrors && snackbarState.show) {
          dismissSnackbar();
        }
        return newErrorState;
      });
    } else {
      // Clear field error when user starts typing (before validation triggered)
      setErrorState((prev) => {
        if (prev.fieldErrors?.[type]) {
          return {
            ...prev,
            fieldErrors: { ...prev.fieldErrors, [type]: false },
            fieldMessages: { ...prev.fieldMessages, [type]: "" },
          };
        }
        return prev;
      });

      // Clear snackbar when user starts typing
      if (snackbarState.show) {
        dismissSnackbar();
      }
    }
  };

  const getTitle = () => {
    return mode === "edit" ? titles?.edit_guest : titles?.add_guest;
  };

  // Check if there are any validation errors
  const hasValidationErrors = Object.values(errorState.fieldErrors).some(error => error);

  // Don't auto-close snackbar if there are validation errors
  const snackbarAutoHideDuration = hasValidationErrors && validationTriggered 
    ? 0 
    : snackbarState?.autoHideDuration;

  return (
    <>
      <MessageSnackBar
        message={snackbarState?.message}
        isVisible={snackbarState?.show}
        onDismiss={dismissSnackbar}
        type={snackbarState?.type}
        autoHideDuration={snackbarAutoHideDuration}
        constrainWidth={true}
      />
      <MainLayout
        text={getTitle()}
        isBackHide={false}
        isSemiHeader={true}
        white={false}
        isLoading={loading}
      >
        <div>
          <p className="aegu_instruction_text">
            {mode !== "edit" ? titles?.add_your_guest : ""}
          </p>
          <p className="aegu_instruction_text">
            {titles?.passwords_must_characters}
          </p>
          
          <div className="aegu_input_group">        
          <p className="aegu_input_label">{titles?.guest_name}</p>
            <CustomInput
              title={titles?.guest_name}
              placeholder="Ex: Jane - Senior Care"
              value={guestUserState?.name}
              onChange={(e) => onValueChange("name", e)}
              error={validationTriggered && errorState.fieldErrors?.name && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.name && errorState.show ? errorState.fieldMessages?.name : ""}
            />
          </div>

          <div className="aegu_input_group">
          <p className="aegu_input_label">
            {titles?.account_holder_email}
          </p>
            <CustomInput
              required={true}
              type={"text"}
              title={titles?.account_holder_email}
              placeholder={`**${accountHolderData?.email}**`}
              disabled={true}
            />
          </div>

          <div className="aegu_input_group">
          <p className="aegu_input_label">{titles?.guest_password}</p>
            <CustomInput
              required={true}
              type={"text"}
              title={titles?.guest_password}
              placeholder={titles?.unique_guest_password}
              value={guestUserState?.password}
              onChange={(e) => onValueChange("password", e)}
              error={validationTriggered && errorState.fieldErrors?.password && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.password && errorState.show ? errorState.fieldMessages?.password : ""}
            />
          </div>

         <div className="aegu_button_group">
           <CustomButton
             text={titles?.save_changes}
             width={"100%"}
             disabled={hasValidationErrors}
             showErrorMessage={validationTriggered && hasValidationErrors}
             onClick={() => {
               onSaveChanges();
             }}
           />
           <CustomButton
             className={"aegu_cancel_btn"}
             text={titles?.cancel}
             width={"100%"}
             onClick={() => {
               onClose();
             }}
           />
         </div>
       </div>
       {verificationPopUp ? (
         <SessionExpiredPopup
           isVisible={verificationPopUp}
           onClosePopup={(parentPopupHide) => {
             setVerificationPopUp(false);
             if (parentPopupHide) {
               onClose();
             }
           }}
           history={history}
         />
       ) : null}
     </MainLayout>
    </>
  );
};

export default AddEditGuestUser;
